import { useState, useEffect, Fragment } from "react";
import { fieldType } from "../../../../types/form";
import { Listbox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { classNames } from "../../../../helpers/classNames";
import { InfoIcon } from "src/components/ui-icons/myTasks/info";
import { TooltipItem } from "src/utils/Tooltip";
import InfoIconSdca from "src/pages/DashboardPage/throughcomponents/card5Way/components/infoIconSdca";
import { twMerge } from "tailwind-merge";

type Props = {
  dataField: fieldType;
  onChange: (data: any) => void;
  value: any;
  errors?: any;
  type?: string;
};

function Select(props: Props) {
  const { dataField, onChange, value, errors, type } = props;
  const [styleSelect, setStyleSelect] = useState<any>(null);
  const [query, setQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<any>([]);

  useEffect(() => {
    if (!query) {
      setFilteredOptions(dataField.options);
    } else {
      setFilteredOptions(
        dataField.options?.filter((option: any) =>
          option.name.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
  }, [JSON.stringify(dataField), query]);

  useEffect(() => {
    if (dataField.key === "MNLabelType" && value) {
      const tempSelectValueStr = value.id;
      if (tempSelectValueStr === "Опасно") {
        setStyleSelect({
          bg: { border: "1px solid #FDE5D8", background: "#FEEBEB" },
          text: { color: "#E10E0E" },
        });
      }
      if (tempSelectValueStr === "Трудно") {
        setStyleSelect({
          bg: { border: "1px solid #DEE2E6", background: "#CED4DA" },
          text: { color: "#000" },
        });
      }
      if (tempSelectValueStr === "Неисправно") {
        setStyleSelect({
          bg: { border: "1px solid #C3CEF6", background: "#E1E8FF" },
          text: { color: "#012169" },
        });
      }
      if (tempSelectValueStr === "Грязный") {
        setStyleSelect({
          bg: { border: "1px solid #FEF3C7", background: "#FFFBEB" },
          text: { color: "##D97706" },
        });
      }
    }
    if (dataField.key === 'SDCA' && !type) {
      setStyleSelect({
        bg: { height: "38px", minHeight: "38px", display: 'flex', alignItems: 'center' },
        text: { color: "#111928" },
      });
    }
  }, [value, dataField]);

  return (
    <div
      className={`${dataField.width} ${(dataField.key !== "periodOWD" && "mb-5 custom-form") || "custom-form"
        }`}
    >
      <Listbox
        multiple={dataField.multiple}
        disabled={dataField.disabled}
        by="id"
        value={value}
        onChange={(value) => {
          onChange(value);
          if (!dataField.multiple) {
            setQuery(""); // сбрасываем только в single режиме
          }
        }}
      >
        {({ open }) => {
          return (
            <>
              <Listbox.Label className="flex flex-row gap-1 text-sm font-medium leading-6 text-gray-900">
                {dataField.key !== "periodOWD" && (
                  <span className="flex flex-row items-center gap-2">
                    {dataField.title}{" "}
                    {dataField.noticeIcon && (
                      <>
                        {dataField.key === 'SDCA' &&
                          <InfoIconSdca />
                          ||
                          <TooltipItem
                            wrapperClassName="flex"
                            position="top-right"
                            tooltipsComponent={dataField.notice}
                            tooltipsText=""
                          >
                            <>
                              &nbsp;&nbsp;
                              <InfoIcon />
                            </>
                          </TooltipItem>
                        }
                      </>
                    )}
                  </span>
                )}
                {dataField.require && (
                  <div className="flex flex-row gap-1">
                    <span className="text-[#BA0C2F]">*</span>
                    {errors &&
                      Object.keys(errors).length > 0 &&
                      errors[dataField.key] && (
                        <span className="text-[#BA0C2F]">
                          Заполните поле&nbsp;
                        </span>
                      )}
                  </div>
                )}
              </Listbox.Label>
              <div
                className={`relative ${(dataField.key !== "periodOWD" && "mt-2") || ""
                  }`}
              >
                <Listbox.Button
                  style={styleSelect && styleSelect.bg}
                  className="min-h-[50px] bg-white text-left cursor-default block w-full text-base rounded-md border border-[#DFE4EA] py-3 px-5 text-black outline-none shadow-custom disabled:cursor-not-allowed disabled:text-[#6B7280] disabled:bg-[#E5E7EB]"
                >
                  <span className={twMerge(
                    "flex flex-wrap gap-2",
                    dataField.key === 'SDCA' && !type && "text-sm"
                  )}>
                    {dataField.multiple ? (
                      value?.length ? (
                        value.map((item: any) => (
                          <span
                            key={item.id}
                            className="flex items-center gap-1 px-2 py-1 text-sm rounded bg-[#F3F4F6]"
                          >
                            {item.name}

                            <XMarkIcon
                              className="h-4 w-4 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                onChange(
                                  value.filter((v: any) => v.id !== item.id)
                                );
                              }}
                            />
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400">
                          {dataField.placeholder}
                        </span>
                      )
                    ) : value ? (
                      value.name
                    ) : (
                      <span className="text-gray-400">
                        {dataField.placeholder}
                      </span>
                    )}
                  </span>
                  {!value && (
                    <span
                      className={`pointer-events-none absolute inset-y-0 right-[20px] top-0 flex items-center ${(dataField.key !== "periodOWD" && "pr-2") || ""
                        }`}
                    >
                      <ChevronUpDownIcon
                        style={styleSelect && styleSelect.text}
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  )}
                </Listbox.Button>
                {(type === "audit_filters_my_tasks" || (dataField.key === 'SDCA' && !type)) && value && (
                  <span className="z-20 cursor-pointer absolute inset-y-0 right-[0px] top-[3px] w-[40px] h-[85%] bg-white flex items-center">
                    <XMarkIcon
                      onClick={(e) => {
                        onChange(dataField.multiple ? [] : null);
                      }}
                      style={styleSelect && styleSelect.text}
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                )}
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {(dataField.searchable || dataField.multiple) && (
                      <div className="px-3 py-2 bg-white">
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Поиск..."
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    )}
                    {dataField.multiple && (
                      <div className="px-3 pb-2">
                        <div className="text-xs text-gray-500 mb-2">
                          Найдено{dataField.key === "Workplaces" && " рабочих мест"}
                          : {filteredOptions?.length || 0}
                        </div>

                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const availableOptions = filteredOptions.filter(
                                (option: any) => option.available !== false
                              );
                              const currentValue = value || [];
                              const newValue = [
                                ...currentValue,
                                ...availableOptions.filter(
                                  (option: any) =>
                                    !currentValue.some((selected: any) => selected.id === option.id)
                                ),
                              ];
                              onChange(newValue);
                            }}
                            className="text-xs text-darkblue hover:underline"
                          >
                            Выбрать все
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const currentValue = value || [];
                              const filteredIds = new Set(
                                filteredOptions.map((option: any) => option.id)
                              );
                              const newValue = currentValue.filter(
                                (selected: any) => !filteredIds.has(selected.id)
                              );
                              onChange(newValue);
                            }}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Снять все
                          </button>
                        </div>
                      </div>
                    )}
                    {filteredOptions &&
                      filteredOptions.map((option: any) => (
                        <Listbox.Option
                          key={option.id}
                          disabled={option.available === false}
                          className={({ active }) =>
                            classNames(
                              active ? "bg-darkblue text-white" : "text-gray-900",
                              "relative cursor-default select-none py-2 pl-3 pr-9",
                              option.available === false
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            )
                          }
                          value={option}
                        >
                          {({ selected, active }) => (
                            <div className="flex items-center gap-2">
                              {dataField.multiple && (
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  readOnly
                                  className="h-4 w-4"
                                />
                              )}

                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "block truncate"
                                )}
                              >
                                {option.name}
                              </span>

                              {!dataField.multiple && selected && (
                                <span
                                  className={classNames(
                                    active ? "text-white" : "text-indigo-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              )}
                            </div>
                          )}
                        </Listbox.Option>
                      ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )
        }}
      </Listbox>
      {!dataField.noticeIcon && dataField.notice && (
        <div className="text-sm text-[#6B7280]">{dataField.notice}</div>
      )}
    </div>
  );
}

export default Select;
